import { useState, useEffect } from 'react';
import { useAuthStore } from '@/features/auth';
import { api } from '@/services/api';
import type { SystemSettings } from '@/contexts/SystemSettingsContext';
import type { SystemTypographySettings, TypographyCategory } from '@/features/admin/types/typography';
import i18n from '@/i18n';

/**
 * Loading stages for progress indication
 */
export type LoadingStage =
  | 'initializing'
  | 'checking-connection'
  | 'hydrating-store'
  | 'loading-settings'
  | 'applying-theme'
  | 'ready'
  | 'error';

/**
 * Emergency fallback configuration from environment variables
 */
const EMERGENCY_CONFIG = {
  theme: import.meta.env.VITE_EMERGENCY_THEME || 'videogame',
  layout: import.meta.env.VITE_EMERGENCY_LAYOUT || 'sidebar',
  locale: import.meta.env.VITE_EMERGENCY_LOCALE || 'he',
  rtl: import.meta.env.VITE_EMERGENCY_RTL !== 'false', // true by default, false only if explicitly set to 'false'
} as const;

/**
 * Apply RTL to document
 */
const applyRTL = (rtlEnabled: boolean): void => {
  if (rtlEnabled) {
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.classList.add('rtl');
  } else {
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.classList.remove('rtl');
  }
};

/**
 * Apply emergency fallback configuration
 */
const applyEmergencyConfig = async (): Promise<void> => {
  console.warn('🚨 [ConfigLoader] Applying emergency fallback configuration');

  // Apply emergency theme
  document.documentElement.setAttribute('data-theme', EMERGENCY_CONFIG.theme);

  // Apply emergency RTL
  applyRTL(EMERGENCY_CONFIG.rtl);

  // Apply emergency locale
  await i18n.changeLanguage(EMERGENCY_CONFIG.locale);
  document.documentElement.setAttribute('lang', EMERGENCY_CONFIG.locale);

  // Store emergency config in localStorage for consistency
  localStorage.setItem('bodywave-theme', EMERGENCY_CONFIG.theme);
  localStorage.setItem('bodywave-layout-mode', EMERGENCY_CONFIG.layout);
};

/**
 * Apply typography settings as CSS variables
 */
const applyTypography = (settings: SystemSettings): void => {
  const root = document.documentElement;

  if (settings.themeFontFamily) {
    root.style.setProperty('--font-family', settings.themeFontFamily);
  }

  const fontSizeMap = { sm: '14px', md: '16px', lg: '18px' };
  const baseFontSize = fontSizeMap[settings.themeFontSize as keyof typeof fontSizeMap] || fontSizeMap.md;
  root.style.setProperty('--font-size-base', baseFontSize);

  if (settings.fontWeightHeading) {
    root.style.setProperty('--font-weight-heading', settings.fontWeightHeading);
  }
  if (settings.fontWeightBody) {
    root.style.setProperty('--font-weight-body', settings.fontWeightBody);
  }
};

/**
 * Apply size settings as CSS variables
 */
const applySizes = (settings: SystemSettings): void => {
  const root = document.documentElement;

  if (settings.baseFontSize) {
    root.style.setProperty('--size-font-base', `${settings.baseFontSize}px`);
  }
  if (settings.headingFontSize) {
    root.style.setProperty('--size-font-heading', `${settings.headingFontSize}px`);
  }
  if (settings.smallFontSize) {
    root.style.setProperty('--size-font-small', `${settings.smallFontSize}px`);
  }
  if (settings.containerMaxWidth) {
    root.style.setProperty('--size-container-max-width', `${settings.containerMaxWidth}rem`);
  }
  if (settings.containerPadding !== undefined) {
    root.style.setProperty('--size-container-padding', `${settings.containerPadding}rem`);
  }
  if (settings.cardPadding !== undefined) {
    root.style.setProperty('--size-card-padding', `${settings.cardPadding}rem`);
  }
  if (settings.cardHeaderPadding !== undefined) {
    root.style.setProperty('--size-card-header-padding', `${settings.cardHeaderPadding}rem`);
  }
  if (settings.cardBodyPadding !== undefined) {
    root.style.setProperty('--size-card-body-padding', `${settings.cardBodyPadding}rem`);
  }
  if (settings.tableHeaderPaddingY !== undefined) {
    root.style.setProperty('--size-table-header-padding-y', `${settings.tableHeaderPaddingY}rem`);
  }
  if (settings.tableHeaderPaddingX !== undefined) {
    root.style.setProperty('--size-table-header-padding-x', `${settings.tableHeaderPaddingX}rem`);
  }
  if (settings.tableCellPaddingY !== undefined) {
    root.style.setProperty('--size-table-cell-padding-y', `${settings.tableCellPaddingY}rem`);
  }
  if (settings.tableCellPaddingX !== undefined) {
    root.style.setProperty('--size-table-cell-padding-x', `${settings.tableCellPaddingX}rem`);
  }
  if (settings.formInputPadding !== undefined) {
    root.style.setProperty('--size-form-input-padding', `${settings.formInputPadding}rem`);
  }
  if (settings.buttonPadding !== undefined) {
    root.style.setProperty('--size-button-padding', `${settings.buttonPadding}rem`);
  }
};

/**
 * Apply comprehensive typography settings as CSS variables
 * Applies base settings (font-family, font-size, font-weight, font-style) for all 12 categories
 */
const applyComprehensiveTypography = (typographySettings?: SystemTypographySettings): void => {
  if (!typographySettings) return;

  const root = document.documentElement;
  const categories: TypographyCategory[] = [
    'navigation', 'pageTitle', 'sectionTitle', 'cardTitle', 'cardContent',
    'body', 'secondaryText', 'button', 'inputLabel', 'inputField', 'utility', 'brand'
  ];

  categories.forEach(category => {
    const config = typographySettings[category];
    if (!config) return;

    // Apply Hebrew base settings
    if (config.hebrew) {
      root.style.setProperty(`--typo-${category}-he-font-family`, config.hebrew.fontFamily);
      root.style.setProperty(`--typo-${category}-he-font-size`, config.hebrew.fontSize);
      root.style.setProperty(`--typo-${category}-he-font-weight`, config.hebrew.fontWeight);
      root.style.setProperty(`--typo-${category}-he-font-style`, config.hebrew.fontStyle);
    }

    // Apply English base settings
    if (config.english) {
      root.style.setProperty(`--typo-${category}-en-font-family`, config.english.fontFamily);
      root.style.setProperty(`--typo-${category}-en-font-size`, config.english.fontSize);
      root.style.setProperty(`--typo-${category}-en-font-weight`, config.english.fontWeight);
      root.style.setProperty(`--typo-${category}-en-font-style`, config.english.fontStyle);
    }

    // Apply advanced styling (shared or per-language)
    if (config.advancedStyling?.shared) {
      const shared = config.advancedStyling.shared;
      if (shared.lineHeight) root.style.setProperty(`--typo-${category}-line-height`, shared.lineHeight);
      if (shared.letterSpacing) root.style.setProperty(`--typo-${category}-letter-spacing`, shared.letterSpacing);
      if (shared.textTransform) root.style.setProperty(`--typo-${category}-text-transform`, shared.textTransform);
      if (shared.color) root.style.setProperty(`--typo-${category}-color`, shared.color);
    }
  });

  console.log('✅ [ConfigLoader] Comprehensive typography settings applied');
};

/**
 * Unified Configuration Loader Hook
 *
 * Orchestrates all configuration loading in correct sequence:
 * 1. Wait for Zustand store hydration
 * 2. Fetch SystemSettings from backend
 * 3. Apply settings (theme, RTL, typography, sizes)
 * 4. If backend fails, apply emergency fallback
 * 5. Only return isReady=true when everything is loaded
 *
 * This prevents FOUC by ensuring UI doesn't render until config is ready
 */
export const useUnifiedConfigLoader = () => {
  const [stage, setStage] = useState<LoadingStage>('initializing');
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Zustand hydration status
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  useEffect(() => {
    // Stage 1: Wait for Zustand hydration
    // Don't start loading configuration until Zustand has fully hydrated
    if (!hasHydrated) {
      setStage('hydrating-store');
      console.log('⏳ [ConfigLoader] Waiting for Zustand hydration...');
      return; // Exit early, effect will re-run when hasHydrated becomes true
    }

    // Zustand is hydrated, proceed with configuration loading
    const loadConfiguration = async () => {
      try {
        console.log('✅ [ConfigLoader] Zustand hydrated');

        // Stage 2: Load SystemSettings from backend
        setStage('loading-settings');
        console.log('⏳ [ConfigLoader] Loading system settings from backend...');

        const response = await api.get('/system/settings');
        const data = response.data.data as SystemSettings;

        setSettings(data);
        console.log('✅ [ConfigLoader] System settings loaded:', data);

        // Stage 3: Apply settings
        setStage('applying-theme');
        console.log('⏳ [ConfigLoader] Applying settings...');

        // Apply i18n language
        if (data.locale && i18n.language !== data.locale) {
          await i18n.changeLanguage(data.locale);
        }

        // Apply RTL
        applyRTL(data.rtlEnabled);

        // Apply typography (legacy)
        applyTypography(data);

        // Apply sizes
        applySizes(data);

        // Apply comprehensive typography settings
        applyComprehensiveTypography(data.typographySettings);

        // Apply locale to document
        document.documentElement.setAttribute('lang', data.locale);

        console.log('✅ [ConfigLoader] Settings applied');

        // Stage 4: Ready!
        setStage('ready');
        console.log('🎉 [ConfigLoader] Configuration complete, app ready to render');

      } catch (err) {
        console.error('❌ [ConfigLoader] Failed to load configuration:', err);
        setError(err instanceof Error ? err : new Error('Configuration loading failed'));
        setStage('error');

        // Apply emergency fallback configuration
        await applyEmergencyConfig();

        // After applying emergency config, mark as ready
        // This allows the app to function even without backend
        setStage('ready');
        console.log('✅ [ConfigLoader] Emergency fallback applied, app ready');
      }
    };

    loadConfiguration();
  }, [hasHydrated]);

  return {
    /**
     * Current loading stage
     */
    stage,

    /**
     * Is configuration ready? (safe to render app)
     */
    isReady: stage === 'ready',

    /**
     * Is loading in progress?
     */
    isLoading: stage !== 'ready' && stage !== 'error',

    /**
     * System settings (null if not loaded or emergency fallback)
     */
    settings,

    /**
     * Error if loading failed (before emergency fallback)
     */
    error,

    /**
     * User-friendly stage message for progress indication
     */
    stageMessage: {
      'initializing': 'Initializing...',
      'checking-connection': 'Connecting to services...',
      'hydrating-store': 'Loading preferences...',
      'loading-settings': 'Loading system settings...',
      'applying-theme': 'Applying theme...',
      'ready': 'Ready',
      'error': 'Error (using fallback)',
    }[stage],
  };
};
